import React, {
	useState,
	useCallback,
	useEffect,
	type ComponentPropsWithRef,
	type RefAttributes,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css, type StyledComponentClass } from 'styled-components';
import { useCrossFlow, Journeys, Targets } from '@atlassiansox/cross-flow-support';
import queryString from 'query-string';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { BitbucketLogo } from '@atlaskit/logo';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { colors, typography } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import GithubLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/github-invertocat-logomark';
import GitlabLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/gitlab-logomark';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { main_softwareCodePage_ConnectorSection$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_ConnectorSection.graphql';
import { useEnvironment, useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { hasBitbucketProvisioned } from '../../../../../services/license-information';
import github from './assets/github.png';
import gitlab from './assets/gitlab.png';
import bbb from './assets/icon-bbb.png';
import messages from './messages';
import { PermissionPopup } from './permission-popup';

type ConnectorSectionProps = {
	connectorSectionData: main_softwareCodePage_ConnectorSection$key;
	isAdmin: boolean;
	marketplaceSectionElement: HTMLButtonElement | null;
	onShowOneClickConnect: () => void;
	onRefetch: () => void;
};

const ProviderImage = ({
	Src,
	legacySrc,
}: {
	Src: React.FC<{ alt: string }>;
	legacySrc: string;
}) =>
	ff('isotopes-consolidate-jfe-assets_pddkf') ? (
		<ProviderImageWrapper>
			<Src alt="" />
		</ProviderImageWrapper>
	) : (
		<LegacyProviderImage src={legacySrc} />
	);

export const ConnectorSection = ({
	connectorSectionData,
	isAdmin,
	marketplaceSectionElement,
	onShowOneClickConnect,
	onRefetch,
}: ConnectorSectionProps) => {
	const { formatMessage } = useIntl();
	const environment = useEnvironment();
	const { fireInteractedUIEvent, fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_ConnectorSection on Query {
				codeInJira(cloudId: $cloudId) @required(action: THROW) {
					userConfiguration @required(action: THROW) {
						ownedBitbucketWorkspaces @required(action: THROW) {
							__typename
						}
					}
				}
			}
		`,
		connectorSectionData,
	);
	const crossFlow = useCrossFlow();
	const tenantContext = useTenantContext();
	const { cloudId } = tenantContext;

	const [dialogOpen, toggleDialog] = useState(false);

	const fireInteractedUIEventSurfaceAnalytics = useCallback(() => {
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectToBitbucketButton',
			},
			{
				targetProductId: Targets.BITBUCKET,
				recommendedProductIds: [Targets.BITBUCKET],
				source: 'connectorSection',
			},
		);
	}, [fireInteractedUIEvent]);

	const showNotASiteAdminDialog = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			toggleDialog(true);

			fireInteractedUIEventSurfaceAnalytics();
			fireUIAnalytics(analyticsEvent, 'codeMainShowNotASiteAdminDialog');
		},
		[fireInteractedUIEventSurfaceAnalytics],
	);

	const closeDialog = useCallback(() => toggleDialog(false), []);

	const analyticsTrackingQueryParams = queryString.stringify({
		utm_source: 'jira-software',
		utm_medium: 'integrations',
		utm_campaign: 'code-in-jira',
		utm_content: 'textlink',
	});

	const BITBUCKET_SIGNUP_LINK =
		environment === 'prod'
			? `https://www.atlassian.com/try/cloud/signup?bundle=jira-software-bitbucket&${analyticsTrackingQueryParams}`
			: `https://wac.stg.internal.atlassian.com/try/cloud/signup?bundle=jira-software-bitbucket&${analyticsTrackingQueryParams}`;

	const switchToOneClickConnect = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			onShowOneClickConnect();
			fireUIAnalytics(analyticsEvent, 'codeMainSwitchToOneClickConnect');
		},
		[onShowOneClickConnect],
	);

	// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
	const switchToBundlePage = useCallback((_, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'codeMainSwitchToBundlePage');
	}, []);

	const redirectToBundlePageButton = (
		<StyledButton
			appearance="primary"
			onClick={switchToBundlePage}
			href={BITBUCKET_SIGNUP_LINK}
			target="_blank"
		>
			{formatMessage(messages.connectToBitbucketButtonLabel)}
		</StyledButton>
	);

	const redirectToOneClickConnectPageButton = (
		<StyledButton appearance="primary" onClick={switchToOneClickConnect} target="_blank">
			{formatMessage(messages.connectToBitbucketButtonLabel)}
		</StyledButton>
	);

	const scrollToMarketplaceSection = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			marketplaceSectionElement && marketplaceSectionElement.scrollIntoView(false);
			fireUIAnalytics(analyticsEvent, 'codeMainConnectToOtherProviders');
		},
		[marketplaceSectionElement],
	);

	const openCrossflow = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
		(_, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'crossflowClicked');
			// UCIA: Fire an analytics event for clicking Connect to Bitbucket button
			fireInteractedUIEventSurfaceAnalytics();
			if (crossFlow.isEnabled) {
				crossFlow.api
					.open({
						journey: Journeys.GET_STARTED,
						targetProduct: Targets.BITBUCKET,
						sourceComponent: 'codeInJira',
						sourceContext: 'jira',
					})
					.then(async () => {
						// Refresh the Code In Jira once BB is provisioned
						const result = await hasBitbucketProvisioned(cloudId);
						if (result) {
							onRefetch();
							fireTrackAnalytics(analyticsEvent, 'codeMainCrossflow expandComplete', {
								hasBitbucketProvisioned: result,
							});
						}
					});
			}
		},
		[cloudId, crossFlow, onRefetch, fireInteractedUIEventSurfaceAnalytics],
	);

	const notAnAdminButton = (
		<StyledButton appearance="primary" onClick={showNotASiteAdminDialog} target="_blank">
			{formatMessage(messages.connectToBitbucketButtonLabel)}
		</StyledButton>
	);

	let connectToBitbucketButton;

	if (!isAdmin) {
		connectToBitbucketButton = notAnAdminButton;
	} else if (data.codeInJira.userConfiguration.ownedBitbucketWorkspaces.length === 0) {
		if (crossFlow.isEnabled) {
			connectToBitbucketButton = (
				<StyledButton appearance="primary" onClick={openCrossflow} target="_blank">
					{formatMessage(messages.connectToBitbucketButtonLabel)}
				</StyledButton>
			);
		} else {
			connectToBitbucketButton = redirectToBundlePageButton;
		}
	} else {
		connectToBitbucketButton = redirectToOneClickConnectPageButton;
	}

	useEffect(() => {
		// UCIA: Fire an analytics event for viewing Connect to Bitbucket section
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'section',
				actionSubjectId: 'connector',
			},
			{
				source: 'jiraCodeScreen',
				recommendedProductIds: [Targets.BITBUCKET],
			},
		);
	}, [fireViewedUIEvent]);

	return (
		<SectionWrapper>
			<TitleContainer>{formatMessage(messages.title)}</TitleContainer>
			<ContentWrapper>
				{/* @ts-expect-error - TS2741 - Property 'alignItems' is missing in type '{ children: Element[]; }' but required in type 'Readonly<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { alignItems: any; }, any>>'. */}
				<RowContainer>
					<ColumnContainer>
						<RowContainer alignItems="flex-end">
							<BitbucketLogo size="large" appearance="brand" />
							<Box xcss={bitbucketLogoSubtextStyles}>
								<Lozenge appearance="inprogress">{formatMessage(messages.subtitle)}</Lozenge>
							</Box>
						</RowContainer>
					</ColumnContainer>

					<ColumnContainer>
						<ImagePanel>
							<ProviderImage Src={GithubLogomark} legacySrc={github} />
							{ff('isotopes-consolidate-jfe-assets_pddkf') || <LegacyProviderImage src={bbb} />}
							<ProviderImage Src={GitlabLogomark} legacySrc={gitlab} />
						</ImagePanel>
					</ColumnContainer>
				</RowContainer>

				{/* @ts-expect-error - TS2741 - Property 'alignItems' is missing in type '{ children: Element[]; }' but required in type 'Readonly<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { alignItems: any; }, any>>'. */}
				<RowContainer>
					<ColumnContainer>
						{connectToBitbucketButton}
						<PermissionPopup isOpen={dialogOpen} onClose={closeDialog} />
					</ColumnContainer>
					<ColumnContainer>
						<StyledButton onClick={scrollToMarketplaceSection}>
							{formatMessage(messages.connectToOtherProvidersButtonLabel)}
						</StyledButton>
					</ColumnContainer>
				</RowContainer>
			</ContentWrapper>
		</SectionWrapper>
	);
};

type ButtonProps = ComponentPropsWithRef<typeof Button>;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionWrapper = styled2.div({
	height: '100%',
	padding: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N20),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled2.div({
	padding: `${token('space.200', '16px')} ${token('space.200', '16px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.text.inverse', colors.N0),
	borderRadius: token('space.100', '8px'),
	boxShadow: `0px 0px 1px ${token('color.border', 'rgba(9, 30, 66, 0.31)')}, 0px 1px 1px ${token(
		'color.border',
		'rgba(9, 30, 66, 0.25)',
	)}`,
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled.div<any>`
	${typography.h400};
	font-size: 14px;
	margin: 0 0 ${token('space.200', '16px')} 0;
	color: ${token('color.text.subtle', colors.N500)};
	text-transform: inherit;
`;

TitleContainer.displayName = 'TitleContainer';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnContainer = styled2.div({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	width: '50%',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowContainer = styled.div<{ alignItems: any }>(
	{
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ alignItems }) =>
		alignItems &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			alignItems,
		}),
);

const bitbucketLogoSubtextStyles = xcss({
	marginLeft: 'space.100',
	marginBottom: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImagePanel = styled2.span({
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProviderImageWrapper = styled2.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: token('space.400', '32px'),
	width: token('space.400', '32px'),
	marginRight: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegacyProviderImage = styled2.img({
	display: 'flex',
	marginRight: token('space.200', '16px'),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton: StyledComponentClass<ButtonProps & RefAttributes<HTMLElement>, any> = styled(
	Button,
)({
	marginRight: token('space.100', '8px'),
	display: 'flex',
	justifyContent: 'center',
	marginTop: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = { StyledButton };
